import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PatientJourneyFunnel = () => {
  const [selectedStage, setSelectedStage] = useState(null);

  const journeyStages = [
    {
      id: 'admission',
      label: 'Patient Admission',
      count: 2847,
      percentage: 100,
      color: 'var(--color-primary)',
      description: 'Initial patient registration and intake',
      outcomes: {
        successful: 2847,
        pending: 0,
        failed: 0
      }
    },
    {
      id: 'assessment',
      label: 'Initial Assessment',
      count: 2789,
      percentage: 98,
      color: 'var(--color-novus-green)',
      description: 'Medical evaluation and diagnosis',
      outcomes: {
        successful: 2789,
        pending: 58,
        failed: 0
      }
    },
    {
      id: 'treatment',
      label: 'Treatment Plan',
      count: 2654,
      percentage: 93,
      color: 'var(--color-warning)',
      description: 'Active treatment and care delivery',
      outcomes: {
        successful: 2654,
        pending: 135,
        failed: 0
      }
    },
    {
      id: 'monitoring',
      label: 'Progress Monitoring',
      count: 2543,
      percentage: 89,
      color: 'var(--color-accent)',
      description: 'Ongoing patient monitoring and adjustments',
      outcomes: {
        successful: 2543,
        pending: 111,
        failed: 0
      }
    },
    {
      id: 'discharge',
      label: 'Discharge Planning',
      count: 2398,
      percentage: 84,
      color: 'var(--color-success)',
      description: 'Discharge preparation and follow-up',
      outcomes: {
        successful: 2398,
        pending: 145,
        failed: 0
      }
    }
  ];

  const handleStageClick = (stage) => {
    setSelectedStage(selectedStage?.id === stage?.id ? null : stage);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 medical-shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Patient Journey Funnel</h3>
          <p className="text-sm text-text-secondary">Track patient progression through care stages</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={16} className="text-text-secondary" />
          <select className="text-sm border border-border rounded px-2 py-1 bg-background">
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
            <option>Last 6 Months</option>
          </select>
        </div>
      </div>
      <div className="space-y-4">
        {journeyStages?.map((stage, index) => (
          <div key={stage?.id} className="relative">
            {/* Funnel Stage */}
            <div
              className="relative cursor-pointer medical-transition hover:opacity-80"
              onClick={() => handleStageClick(stage)}
            >
              <div
                className="h-16 rounded-lg flex items-center justify-between px-6 text-white font-medium"
                style={{
                  backgroundColor: stage?.color,
                  width: `${stage?.percentage}%`,
                  minWidth: '300px'
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <div className="font-semibold">{stage?.label}</div>
                    <div className="text-sm opacity-90">{stage?.count?.toLocaleString()} patients</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">{stage?.percentage}%</div>
                  <div className="text-sm opacity-90">completion</div>
                </div>
              </div>
              
              {/* Connection Line */}
              {index < journeyStages?.length - 1 && (
                <div className="absolute left-1/2 -bottom-2 w-0.5 h-4 bg-border transform -translate-x-1/2" />
              )}
            </div>

            {/* Expanded Details */}
            {selectedStage?.id === stage?.id && (
              <div className="mt-4 ml-8 p-4 bg-muted rounded-lg border-l-4" style={{ borderLeftColor: stage?.color }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-medium text-text-primary mb-2">Stage Details</h4>
                    <p className="text-sm text-text-secondary">{stage?.description}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary mb-2">Outcomes</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-success">Successful:</span>
                        <span className="font-medium">{stage?.outcomes?.successful?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-warning">Pending:</span>
                        <span className="font-medium">{stage?.outcomes?.pending?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-destructive">Failed:</span>
                        <span className="font-medium">{stage?.outcomes?.failed?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary mb-2">Actions</h4>
                    <div className="space-y-2">
                      <button className="text-sm text-primary hover:underline block">View Patient Cohort</button>
                      <button className="text-sm text-primary hover:underline block">Export Data</button>
                      <button className="text-sm text-primary hover:underline block">Set Alerts</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">84%</div>
            <div className="text-sm text-text-secondary">Overall Completion</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">2.3</div>
            <div className="text-sm text-text-secondary">Avg Days/Stage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">449</div>
            <div className="text-sm text-text-secondary">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">96%</div>
            <div className="text-sm text-text-secondary">Success Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientJourneyFunnel;